import { Fragment } from 'react';
import { Text } from '@spark';

const BoldName = ({ children }) => (
    <Text variant="base-semibold" as="strong">
        {children}
    </Text>
);

const boldNameList = (names) =>
    names.map((name, index) => (
        <Fragment key={`${name}-${index}`}>
            {index > 0 ? ', ' : null}
            <BoldName>{name}</BoldName>
        </Fragment>
    ));

/** Card-facing task names for a project: unique `sub_group_name`, else usecase_name. */
export const uniqueProjectTaskNames = (app) => {
    const names = [];
    const seen = new Set();
    for (const subGroup of app?.sub_groups || []) {
        const name =
            (subGroup?.sub_group_name || '').trim() ||
            subGroup?.usecases?.[0]?.usecase_name ||
            'Task';
        if (!seen.has(name)) {
            seen.add(name);
            names.push(name);
        }
    }
    return names;
};

/**
 * Success toast after deleting task(s).
 * Single: "Task <name> deleted successfully"
 * Multiple: "<n> tasks deleted successfully: <name>, ..."
 */
export const deletedTasksToastTitle = (taskNames = []) => {
    const names = Array.isArray(taskNames) ? taskNames : [];

    if (names.length <= 1) {
        return (
            <>
                Task <BoldName>{names[0] ?? ''}</BoldName> deleted successfully
            </>
        );
    }

    return (
        <>
            {names.length} tasks deleted successfully: {boldNameList(names)}
        </>
    );
};

/** User-facing task name: card title (`sub_group_name`) else `usecase_name`. */
export const displayTaskName = (usecase) =>
    (usecase?.sub_group_name || '').trim() || usecase?.usecase_name || 'Task';

export const uniqueTaskNamesFromUsecases = (usecases = []) => {
    const names = [];
    const seen = new Set();
    for (const usecase of usecases) {
        const name = displayTaskName(usecase);
        if (!seen.has(name)) {
            seen.add(name);
            names.push(name);
        }
    }
    return names;
};

/**
 * Success toast title for project deletion.
 * Copy matches _format_project_deleted_success_message in backend/app/utils/project_deletion.py; names are bold.
 */
export const projectDeletedToastTitle = (projectName, taskNames = []) => {
    const names = Array.isArray(taskNames) ? taskNames : [];
    const project = <BoldName>{projectName}</BoldName>;

    if (names.length <= 1) {
        return (
            <>
                Project {project} deleted successfully. Task <BoldName>{names[0] ?? ''}</BoldName> was also removed.
            </>
        );
    }

    return (
        <>
            Project {project} deleted successfully. {names.length} tasks were also removed:{' '}
            {boldNameList(names)}.
        </>
    );
};
